import { format, formatDistanceToNow, parseISO } from 'date-fns'

/**
 * Parse an ISO date string and convert to local timezone
 * Backend sends UTC timestamps, we need to display them in user's local timezone
 */
export function parseUTCDate(dateString: string | null | undefined): Date | null {
  // Add defensive check at the very beginning
  if (!dateString || typeof dateString !== 'string') {
    return null
  }
  
  try {
    // Check if the string has timezone info (ends with Z or has +/- offset)
    const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(dateString)
    
    if (!hasTimezone && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateString)) {
      // If it's an ISO-like string without timezone, assume it's UTC
      dateString = dateString + 'Z'
    }
    
    // parseISO handles ISO 8601 strings and creates a Date object
    // The Date object automatically uses the local timezone for display
    return parseISO(dateString)
  } catch (error) {
    console.error('Invalid date string passed to parseISO:', dateString, error)
    return null // Return null on parsing error
  }
}

/**
 * Format a date for display in the user's local timezone
 * @param dateString - ISO date string from backend (in UTC)
 * @param formatStr - date-fns format string (default: 'PPpp' for full date/time)
 */
export function formatLocalDate(
  dateString: string | null | undefined, 
  formatStr: string = 'PPpp'
): string {
  const date = parseUTCDate(dateString)
  if (!date) return 'N/A'
  
  try {
    return format(date, formatStr)
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'Invalid date'
  }
}

/**
 * Format a date as relative time (e.g., "2 hours ago")
 * @param dateString - ISO date string from backend (in UTC)
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
  const date = parseUTCDate(dateString)
  if (!date) return 'N/A'
  
  try {
    // Debug log to help identify timezone issues
    if (process.env.NODE_ENV === 'development') {
      const now = new Date()
      const diff = date.getTime() - now.getTime()
      if (diff > 0) {
        console.warn('Date appears to be in the future:', {
          original: dateString,
          parsed: date.toISOString(),
          now: now.toISOString(),
          diffHours: diff / (1000 * 60 * 60)
        })
      }
    }
    
    return formatDistanceToNow(date, { addSuffix: true })
  } catch (error) {
    console.error('Error formatting relative time:', error, { dateString, date })
    return 'N/A'
  }
}

/**
 * Common date format patterns
 */
export const DateFormats = {
  /** Full date and time: "Dec 25, 2023 at 3:30 PM" */
  FULL: 'PPp',
  /** Date only: "Dec 25, 2023" */
  DATE_ONLY: 'PP',
  /** Time only: "3:30 PM" */
  TIME_ONLY: 'p',
  /** Short date: "12/25/23" */
  SHORT_DATE: 'P',
  /** ISO-like: "2023-12-25 15:30:45" */
  TECHNICAL: 'yyyy-MM-dd HH:mm:ss',
  /** Compact: "Dec 25, 3:30 PM" */
  COMPACT: 'MMM d, p',
} as const

/**
 * Get timezone information for display
 */
export function getTimezoneInfo(): string {
  const date = new Date()
  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone
  const offset = -date.getTimezoneOffset()
  const sign = offset >= 0 ? '+' : '-'
  const hours = Math.floor(Math.abs(offset) / 60)
  const minutes = Math.abs(offset) % 60
  
  return `${timezoneName} (UTC${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')})`
}

/**
 * Get timezone abbreviation (e.g., "CDT", "PST", "EST")
 */
export function getTimezoneAbbreviation(): string {
  const date = new Date()
  const timezoneName = date.toLocaleString('en-US', { timeZoneName: 'short' })
  const match = timezoneName.match(/\b[A-Z]{3,4}\b/)
  return match ? match[0] : 'UTC'
}

/**
 * Format a date with timezone abbreviation for display
 * @param dateString - ISO date string from backend (in UTC)
 * @param formatStr - date-fns format string (default: 'MMM d, p' for compact format)
 */
/**
 * Format a date string as "Mon DD, YYYY" (e.g., "Mar 14, 2026").
 * Returns an em-dash if the input is null/undefined/unparseable.
 */
export function formatShortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "\u2014";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "\u2014";
  }
}

/**
 * Format a duration in seconds to a human-readable compact string.
 * Examples: "45s", "3m 12s", "2h 15m", "1d 4h 30m"
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return 'N/A'

  const totalSeconds = Math.round(seconds)
  if (totalSeconds < 60) return `${totalSeconds}s`

  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60

  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h ${minutes}m` : `${days}d ${minutes}m`
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  }
  return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`
}

/**
 * Calculate duration in seconds between two ISO date strings.
 * Returns null if either date is invalid.
 */
export function calculateDurationSeconds(
  startDate: string | null | undefined,
  endDate: string | null | undefined
): number | null {
  const start = parseUTCDate(startDate)
  const end = parseUTCDate(endDate)
  if (!start || !end) return null
  const diffMs = end.getTime() - start.getTime()
  return diffMs > 0 ? diffMs / 1000 : null
}

export function formatLocalDateWithTimezone(
  dateString: string | null | undefined, 
  formatStr: string = 'MMM d, p'
): string {
  const date = parseUTCDate(dateString)
  if (!date) return 'N/A'
  
  try {
    const formattedDate = format(date, formatStr)
    const timezone = getTimezoneAbbreviation()
    return `${formattedDate} ${timezone}`
  } catch (error) {
    console.error('Error formatting date with timezone:', error)
    return 'Invalid date'
  }
}